import {
  Button,
  ButtonColorEnum,
  ClockIcon,
  IconButton,
  IconButtonColorEnum,
  IconSizeEnum,
  Tooltip,
} from '@acceso/design-system';
import { useTranslation } from 'react-i18next';

import { TableRow } from '@/components/TableRow';
import { buildNewPresenceDataToSave } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/helpers/batchSelectionHelpers';
import {
  PostPresenceFor,
  TBroadcastScheduleOnUpdateInline,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { formatDateCustom } from '@/helpers/format-date';
import { useNotificationStore } from '@/stores/notifications';
import { TPresenceItem } from '@/types/presences';

type TimelineStartInlineControlProps<T extends TPresenceItem> = {
  item: T;
  onUpdateInline?: TBroadcastScheduleOnUpdateInline<PostPresenceFor<T>>;
  onPreview?: (item: T) => void;
  buildDataToSave?: (item: T) => PostPresenceFor<T>;
  presenceData?: { id?: string };
};

export function TimelineStartInlineControl<T extends TPresenceItem>({
  item,
  onUpdateInline,
  onPreview,
  buildDataToSave,
  presenceData,
}: TimelineStartInlineControlProps<T>) {
  const { logoData, playerVideoCurrentTime, getDateTimeCurrentPresence } =
    useBroadcastScheduleProgram();
  const { t } = useTranslation();
  const { addErrorNotification } = useNotificationStore();

  const activeData = presenceData ?? logoData;
  const dataToSave = (buildDataToSave ?? buildNewPresenceDataToSave) as (
    item: T
  ) => PostPresenceFor<T>;

  const setNewStartDateTime = () => {
    if (activeData?.id !== item.id) {
      onPreview?.(item);
    }

    const newData = dataToSave(item);
    const newDateTime = getDateTimeCurrentPresence(playerVideoCurrentTime);
    const applySilently = false;

    const { end_datetime: endDatetime } = newData;

    if (newDateTime && endDatetime && endDatetime < newDateTime) {
      addErrorNotification(
        t(
          'El tiempo para marcar el inicio de una presencia de una presencia no puede ser posterior al de finalización'
        )
      );
      return;
    }

    newDateTime && (newData.start_datetime = newDateTime);
    onUpdateInline?.(newData, applySilently, playerVideoCurrentTime);
  };

  return (
    <div className="flex items-center gap-0.5">
      <Button
        className="min-h-13 bg-transparent p-2"
        color={ButtonColorEnum.SECONDARY_OLD}
        onClick={() => onPreview?.(item)}
      >
        <TableRow
          content={formatDateCustom({
            input: item.start_datetime,
            format: 'dd/MM/yyyy HH:mm:ss',
          })}
        />
      </Button>
      <Tooltip help={t('Marcar Inicio en el frame de vídeo actual')} grow>
        <IconButton color={IconButtonColorEnum.NONE_DEFAULT} onClick={setNewStartDateTime}>
          <ClockIcon size={IconSizeEnum.SM} />
        </IconButton>
      </Tooltip>
    </div>
  );
}
